/**
 * Asset schemas for one client (B1, kept; RESTORE_PLAN R1.8 gives it a home on the client page's
 * Deliver tab): install a CMDB starter template (AssetSchemaSelector), see each installed schema's
 * object types, attribute counts and discovery mapping, remove one. The installed list is read
 * back from the server; it used to live in this browser's local storage only, and the delete
 * button did nothing.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Plus, Trash2, Database, AlertCircle, Loader2 } from 'lucide-react';
import { AssetSchemaSelector } from './AssetSchemaSelector';
import { toast } from 'sonner';
import {
  discoveryMappingText,
  installSchemaTemplate,
  loadInstalledSchemas,
  removeInstalledSchema,
  type InstalledAssetSchema,
} from '../utils/assetSchemas';

interface AssetConfigurationProps {
  companyId: string;
  accessToken: string;
  /** False for a read-only view: no install or remove. */
  canEdit?: boolean;
}

export function AssetConfiguration({ companyId, accessToken, canEdit = true }: AssetConfigurationProps) {
  const [view, setView] = useState<'list' | 'selector'>('list');
  const [installedSchemas, setInstalledSchemas] = useState<InstalledAssetSchema[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadInstalledSchemas(companyId, accessToken)
      .then((list) => { if (!cancelled) setInstalledSchemas(list); })
      .catch((error) => { if (!cancelled) toast.error(error instanceof Error ? error.message : 'Could not load the asset schemas'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, accessToken]);

  const handleInstallTemplate = async (template: { id: string; name: string }) => {
    try {
      setBusy('install');
      setInstalledSchemas(await installSchemaTemplate(companyId, template.id, accessToken));
      toast.success(`Installed ${template.name}`);
      setView('list');
    } catch (error) {
      console.error('Installation failed', error);
      toast.error(error instanceof Error ? error.message : 'Failed to install schema');
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async (schema: InstalledAssetSchema) => {
    if (!window.confirm(`Remove the "${schema.name}" schema from this client?`)) return;
    try {
      setBusy(schema.id);
      setInstalledSchemas(await removeInstalledSchema(companyId, schema.id, accessToken));
      toast.success(`Removed ${schema.name}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove the schema');
    } finally {
      setBusy(null);
    }
  };

  if (view === 'selector') {
    return (
      <AssetSchemaSelector
        companyId={companyId}
        onSelect={handleInstallTemplate}
        onCancel={() => setView('list')}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="asset-configuration">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-medium">Asset Schemas</h3>
          <p className="text-sm text-muted-foreground">
            Define the structure of this client's CMDB and Assets.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setView('selector')} className="gap-2" disabled={!!busy}>
            {busy === 'install' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Import Schema Template
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : installedSchemas.length === 0 ? (
        <Card className="bg-slate-50 border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-10 text-center">
            <Database className="h-10 w-10 text-muted-foreground mb-4" />
            <h4 className="font-medium text-lg">No Schemas Configured</h4>
            <p className="text-muted-foreground max-w-sm mt-2 mb-6">
              Start by importing a best-practice schema template to define your Object Types and Attributes.
            </p>
            {canEdit && (
              <Button onClick={() => setView('selector')}>
                Browse Templates
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {installedSchemas.map((schema) => (
            <Card key={schema.id} data-testid="installed-asset-schema">
              <CardHeader className="pb-3">
                <div className="flex justify-between">
                  <div className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-base">{schema.name}</CardTitle>
                    <Badge variant="outline">{schema.objectTypes.length} Object Types</Badge>
                  </div>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove schema ${schema.name}`}
                      disabled={busy === schema.id}
                      onClick={() => handleRemove(schema)}
                      className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      {busy === schema.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  )}
                </div>
                <CardDescription>
                  Based on: {schema.sourceTemplate || 'Custom'}{schema.description ? ` • ${schema.description}` : ''}
                  {schema.installedAt ? ` • Installed ${new Date(schema.installedAt).toLocaleDateString()}${schema.installedBy?.name ? ` by ${schema.installedBy.name}` : ''}` : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-sm font-medium">Object Types & Mapping:</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {schema.objectTypes.map((ot) => (
                      <div key={ot.id} className="border rounded-md p-3 bg-slate-50">
                        <div className="flex items-center gap-2 font-medium text-sm mb-2">
                          <span>{ot.name}</span>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1">
                          <div>{ot.attributes?.length ?? 0} attributes</div>
                          {discoveryMappingText(ot) && (
                            <div className="flex items-start gap-1 text-blue-700 bg-blue-50 p-1.5 rounded mt-1">
                              <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
                              <span>{discoveryMappingText(ot)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
